import { z } from "zod"

export type { Project, ProjectInput, UrbanPriority, BuyersList } from "../graphql.ts"
export {
	OpportunityPriority,
	PricingSource,
	OwnerType,
	OwnershipDuration,
	MotivationToSell,
	PotentialDealStructure,
	MainZoning,
	ProjectApprovalProcess,
	MunicipalReceptiveness,
	KnownDevelopmentRestriction,
	MunicipalInfrastructure,
} from "../graphql.ts"
import {
	OpportunityPriority,
	OpportunityStatus,
	OpportunityType,
	PricingSource,
	OwnerType,
	OwnershipDuration,
	MotivationToSell,
	PotentialDealStructure,
	MainZoning,
	ProjectApprovalProcess,
	MunicipalReceptiveness,
	KnownDevelopmentRestriction,
	MunicipalInfrastructure,
	TransactionParkingType,
} from "../graphql.ts"

export { OpportunityType }

export enum EvaluationStatus {
	Like = OpportunityStatus.Like,
	Dislike = OpportunityStatus.Dislike,
	Unset = "unset",
}

export const LayerType = Object.freeze({
	CompletedAssessments: "completed-assessments",
	ListingLanderz: OpportunityType.ListingLanderz,
	ListingPotential: OpportunityType.ListingPotential,
	OffMarketOfficial: OpportunityType.OffMarketOfficial,
	OpportunityTarget: OpportunityType.OpportunityTarget,
	Transaction: "transaction",
} as const)

export type LayerTypeValue = (typeof LayerType)[keyof typeof LayerType]

export enum LayerFeatureType {
	CompletedAssessment = "COMPLETED_ASSESSMENT",
	Tod = "TOD",
	Ppu = "PPU",
	Corridor = "CORRIDOR",
	Strategic = "STRATEGIC",
	Soil = "SOIL",
	TransitLine = "TRANSIT_LINE",
	TransitStation = "TRANSIT_STATION",
	EndangeredSpecies = "ENDANGERED_SPECIES",
	Zoning = "ZONING",
}

export type LayerFeatureSpec = { label: string; value: string | null; isLink?: boolean }

export type LayerFeatureDetail = {
	id: string
	type: LayerFeatureType
	rows: LayerFeatureSpec[]
}

const DevelopmentType = {
	residential: "Résidentiel",
	residentielBois: "Résidentiel - Bois",
	residentielBeton: "Résidentiel - Béton",
	mixed: "Mixte",
	commercial: "Commercial",
	subdivision: "Lotissement",
	unidentified: "Inconnu",
	agricultural: "Agricole",
	industrial: "Industriel",
	institutional: "Institutionnel",
	hotelier: "Hotelier",
	forestier: "Forestier",
	conservation: "Conservation",
} as const

export enum DevelopmentPotential {
	High = "High",
	Medium = "Medium",
	Low = "Low",
	None = "None",
	Unspecified = "Unspecified",
}

export enum Borough {
	Verdun = "Verdun",
	LaBaie = "La Baie",
	LeVieuxLongueuil = "Le Vieux-Longueuil",
	Desjardins = "Desjardins",
	LaHauteSaintCharles = "La Haute-Saint-Charles",
	Grenville = "Grenville",
	CoteDesNeigesNotreDameDeGrace = "Côte-des-Neiges - Notre-Dame-de-Grâce",
	MontrealNord = "Montréal-Nord",
	AhuntsicCartierville = "Ahuntsic-Cartierville",
	Jonquiere = "Jonquière",
	Lachine = "Lachine",
	LesChutesDeLaChaudiereEst = "Les Chutes-de-la-Chaudière-Est",
	PierrefondsRoxboro = "Pierrefonds - Roxboro",
	Calumet = "Calumet",
	Outremont = "Outremont",
	LesRivieres = "Les Rivières",
	SaintLaurent = "Saint-Laurent",
	MacNider = "MacNider",
	Chicoutimi = "Chicoutimi",
	Arrondissement4 = "Arrondissement 4",
	Arrondissement2 = "Arrondissement 2",
	Beauport = "Beauport",
	LeSudOuest = "Le Sud-Ouest",
	SainteFoySilleryCapRouge = "Sainte-Foy - Sillery - Cap-Rouge",
	SaintHubert = "Saint-Hubert",
	Arrondissement1 = "Arrondissement 1",
	RivieredesPrairiesPointeAuxTrembles = "Rivière-des-Prairies - Pointe-aux-Trembles",
	LIleBizardSainteGenevieve = "LÎle-Bizard - Sainte-Geneviève",
	LePlateauMontRoyal = "Le Plateau-Mont-Royal",
	VilleMarie = "Ville-Marie",
	MercierHochelagaMaisonneuve = "Mercier - Hochelaga-Maisonneuve",
	RosemontLaPetitePatrie = "Rosemont - La Petite-Patrie",
	Arrondissement3 = "Arrondissement 3",
	Charlesbourg = "Charlesbourg",
	GreenfieldPark = "Greenfield Park",
	LaSalle = "LaSalle",
	LaCiteLimoilou = "La Cité-Limoilou",
	VilleraySaintMichelParcExtension = "Villeray - Saint-Michel - Parc-Extension",
	SaintLeonard = "Saint-Léonard",
	LesChutesDeLaChaudiereOuest = "Les Chutes-de-la-Chaudière-Ouest",
	Anjou = "Anjou",
}

export enum LayerFeatureType {
	CityPvDocument = "CITY_PV_DOCUMENT",
}

const SalesProcessStatus = {
	open: "open",
	accepted_psa: "accepted_psa",
}

/** The Zod schema used to validate a listing */
export const OpportunitySchema = z.object({
	/// the listing id
	id: z.string(),
	hubspot_record_id: z.string(),
	type: z.nativeEnum(LayerType),
	typeDev: z.array(z.nativeEnum(DevelopmentType)),
	constructible: z.nullable(z.number()),
	superficy: z.number(),
	longitude: z.number(),
	latitude: z.number(),
	price: z.nullable(z.number()),
	pricePerSqft: z.nullable(z.number()),
	pricePerConstructibleSqft: z.nullable(z.number()),
	link: z.nullable(z.string()),
	address: z.string().nullish(),
	city: z.string(),
	state: z.string(),
	region: z.string().nullish(),
	borough: z.string().nullish(),
	postal_code: z.string(),
	image: z.nullable(z.string()),
	off_market_radius: z.number(),
	sales_process_status: z.nativeEnum(SalesProcessStatus),
	launch_date: z.date().optional(),
	launch_date_ms: z.number().optional(),
	call_for_bids: z.boolean(),
	canEdit: z.boolean().default(false),
	broker_email: z.string().nullish(),
	broker_first_name: z.string().nullish(),
	broker_last_name: z.string().nullish(),
	broker_phone_number: z.string().nullish(),
	broker_picture_url: z.string().nullish(),
	collaborative_broker_names: z.array(z.string()).nullish(),
	collaborative_broker_1_name: z.string().nullish(),
	collaborative_broker_1_email: z.string().nullish(),
	collaborative_broker_1_phone: z.string().nullish(),
	collaborative_broker_1_picture: z.string().nullish(),
	collaborative_broker_2_name: z.string().nullish(),
	collaborative_broker_2_email: z.string().nullish(),
	collaborative_broker_2_phone: z.string().nullish(),
	collaborative_broker_2_picture: z.string().nullish(),
	primary_seller: z.string().nullable(),
	lot_numbers_full: z.array(z.number()).nullable(),
	all_owners_names: z.array(z.string()).nullable(),
	project_status: z.string().optional(),
	information_on_existing_conditions: z.string().optional(),
	information_on_potential_and_zoning: z.string().optional(),
	owner_information: z.string().optional(),
	data_room_cloud_file: z.string().optional(),
	modelling_link: z.string().optional(),
	status: z.string().nullish(),
	priority: z.nativeEnum(OpportunityPriority).nullish(),
	marketing_type: z.string(),
	projectId: z.string().nullish(),
	stageLabel: z.string().nullish(),
	parent_opportunity_type: z.string().nullish(),
	isSyncPending: z.boolean().nullish(),
	isPlaceholder: z.boolean().nullish(),
	assetType: z.array(z.string()).nullish(),
	landCategoryNew: z.array(z.string()).nullish(),
	landSubcategory: z.array(z.string()).nullish(),
	priceExpectation: z.number().nullable(),
	pricingSource: z.nativeEnum(PricingSource).nullable(),
	residentialUnits: z.number().nullable(),
	pricePerUnit: z.number().nullable(),
	ownerTypes: z.array(z.nativeEnum(OwnerType)).nullable(),
	ownershipDuration: z.nativeEnum(OwnershipDuration).nullable(),
	motivationsToSell: z.array(z.nativeEnum(MotivationToSell)).nullable(),
	potentialDealStructures: z.array(z.nativeEnum(PotentialDealStructure)).nullable(),
	mainZoning: z.nativeEnum(MainZoning).nullable(),
	projectApprovalProcesses: z.array(z.nativeEnum(ProjectApprovalProcess)).nullable(),
	municipalReceptiveness: z.array(z.nativeEnum(MunicipalReceptiveness)).nullable(),
	lastConversationCity: z.date().nullable(),
	knownDevelopmentRestrictions: z.array(z.nativeEnum(KnownDevelopmentRestriction)).nullable(),
	municipalInfrastructures: z.array(z.nativeEnum(MunicipalInfrastructure)).nullable(),
	lastConversationOwner: z.date().nullable(),
	commentsAboutOwner: z.string().nullable(),
})

/** A listing is the primary thing our app displays. It represents lots
 * suitable for redevelopment on the market,
 * either through Landerz brokerage activities or another broker */
export interface Opportunity extends z.infer<typeof OpportunitySchema> {}

/** The Zod schema used to validate a parcel (GraphQL version) */
export const ParcelSchema = z.object({
	parcelID: z.string(),
	location: z.object({
		coordinates: z.object({
			longitude: z.string(),
			latitude: z.string(),
		}),
	}),
})

export interface Parcel extends z.infer<typeof ParcelSchema> {}

/** The Zod schema used to validate a prospect (GraphQL version) */
export const ProspectSchema = z.object({
	prospectID: z.string(),
	potential: z.nativeEnum(DevelopmentPotential),
})

export interface Prospect extends z.infer<typeof ProspectSchema> {}

export const TransactionSaleContext = [
	"Normal",
	"Linked",
	"Assembly",
	"Lié",
	"Lié, Vente de portefeuille",
	"Lié, Partie d'un portefeuille",
	"Assemblage",
	"Assemblage, Partie d'un portefeuille",
	"Normal, Vente de portefeuille",
	"Normal, Partie d'un portefeuille",
	"Succession",
	"Rapide",
	"Vente par municipalité",
	"Indication hors marché",
	"Autre",
]
export const TransactionPartyType = ["Individual", "Company"]

export const TransactionCurrentState = [
	"Vacant Land",
	"Building to demolish",
	"Building in usable shape",
	"Transformation",
]
export const TransactionCodificationType = [
	"Landerz Estimate",
	"Project plans",
	"Under approval",
	"City Resolution",
	"Project Website",
	"Buyer interview",
	"Built / Under construction",
	"Other",
	"Upcoming",
]
export const TransactionProjectApproval = ["Plein droit", "Modification réglementaire", "Unknown"]
export const TransactionProjectApprovalAtDeedOfSale = ["Completed", "In progress", "Incomplete", "Unknown"]
export const TransactionCPTAQ = ["Inside", "Outside"]
export const TransactionUrbanPerimeter = ["Inside", "Outside"]
export const TransactionInfrastructure = ["Serviced", "Unserviced"]
export const TransactionLandShape = ["Regular", "Irregular"]
export const TransactionContamination = ["Yes", "No", "Probable", "Improbable", "Decontaminated"]

export enum TransactionSource {
	Provided = "provided",
	Imported = "imported",
}

export enum TransactionComposition {
	Assembly = "assembly",
	Standalone = "standalone",
}

export enum TransactionMetadataStatus {
	Pending = "pending",
	InProgress = "in progress",
	Deferred = "deferred",
	Done = "done",
}

export enum TransactionMetadataBuyerStatus {
	Pending = "pending",
	Done = "done",
}

export enum TransactionFieldStatus {
	Surveyed = "surveyed",
	NotApplicable = "not_applicable",
	Unavailable = "unavailable",
	Upcoming = "upcoming",
}

export const TransactionSubcategory = {
	Agricole: "Agricole",
	Commercial: "Commercial",
	LocalHealthClinic: "CLSC",
	Industrial: "Industriel",
	AutoRepairShop: "Garage mécanique",
	Park: "Parc",
	CarDealership: "Concessionnaire",
	ShoppingCenter: "Centre commercial",
	SugarShack: "Érablière",
	Institutional: "Institutionnel",
	Farm: "Ferme",
	Convent: "Couvent",
	Lodging: "Hébergement",
	Parking: "Stationnement",
	Municipal: "Municipal",
	RecreationAndForest: "Récréatif et forêt",
	PlantNursery: "Pépinière",
	SeniorHome: "RPA",
	Infrastructure: "Infrastructure",
	GolfCourse: "Golf",
	Campground: "Camping",
	SpecializedFacility: "Établissement spécialisé",
	MultiUnitResidential: "Multilocatif",
	Condo: "Condo",
	School: "École",
	MixedUse: "Mixte",
	General: "Général",
	Daycare: "Garderie",
	Church: "Église",
	Land: "Terrain",
	RoomingHouse: "Maison de chambres",
	ConvenienceStore: "Dépanneur",
	Quarry: "Carrière",
	Telecommunications: "Télécommunication",
	Residential: "Résidentiel",
} as const

export type TransactionSubcategory = (typeof TransactionSubcategory)[keyof typeof TransactionSubcategory]

// prettier-ignore
const CompanySchema = z.object({
    id:       z.string().nullish(),
    name:     z.string().nullish(),
    address:  z.string().nullish(),
    isMoral:  z.boolean().nullish(),
    neq:      z.string().nullish(),
    type:     z.string().nullish(),
    activity: z.string().nullish(),
})

// prettier-ignore
const ContactSchema = z.object({
    id:        z.string().nullish(),
    firstName: z.string().nullish(),
    lastName:  z.string().nullish(),
    address:   z.string().nullish(),
    email:     z.string().nullish(),

    // Actor
    primaryCompany: CompanySchema.nullish(),

    // From GraphQL Schema ContactTransaction
    companyName:       z.string().nullish(),
    crmContactId:      z.string().nullish(),
    representativeId:  z.string().nullish(),
    crmCompanyId:      z.string().nullish(),
    inscriptionNumber: z.string().nullish(),
    inscriptionNumbers:z.array(z.string()).nullish(),
    transactions:      z.array(z.any()).nullish(),
})

// prettier-ignore
export const ContactListSchema = z.array(ContactSchema)
export const CompanyListSchema = z.array(CompanySchema)

export interface Contact extends z.infer<typeof ContactSchema> {}
export interface ContactList extends z.infer<typeof ContactListSchema> {}

export interface Company extends z.infer<typeof CompanySchema> {}
export interface CompanyList extends z.infer<typeof CompanyListSchema> {}

/** The Zod schema used to validate and transform a transaction */
// prettier-ignore
export const TransactionSchema = z
	.object({
		presentationID: z.string().nullish(),
		parcels: z
			.array(
				z
					.object({
						parcelID: z.string().nullish(),
						isActive: z.boolean().nullish(),
						location: z
							.object({
								coordinates: z
									.object({
										longitude: z.string().nullish(),
										latitude: z.string().nullish(),
									})
									.nullish(),
								address: z.string().nullish(),
							})
							.nullish(),
						superficy: z
							.object({
								squareMeters: z.number().nullish(),
								squareFeet: z.number().nullish(),
							})
							.nullish(),
					})
					.nullish()
			)
			.nullish(),

		contacts: ContactListSchema.nullish(),
		deedOfSaleDate: z.coerce.date().nullish(),
		location: z.object({
			coordinates: z.object({
				longitude: z.string().nullish(),
				latitude: z.string().nullish(),
			}),
			address: z.string().nullish(),
			municipality: z.string().nullish(),
			borough: z.string().nullish(),
		}),
		superficy: z
			.object({
				squareMeters: z.number().nullish(),
				squareFeet: z.number().nullish(),
			})
			.nullish(),
		seller: z
			.object({
				id:              z.number().nullish(),
				name:            z.string().nullish(),
				adresse:         z.string().nullish(),
				isMoral:         z.boolean().nullish(),
				neq:             z.string().nullish(),
                type:            z.string().nullish(),
                activity:        z.string().nullish(),
			})
			.nullish(),
		buyer: z
			.object({
				id:              z.number().nullish(),
				name:            z.string().nullish(),
				adresse:         z.string().nullish(),
				isMoral:         z.boolean().nullish(),
				neq:             z.string().nullish(),
                type:            z.string().nullish(),
                activity:        z.string().nullish(),
			})
			.nullish(),

		id:                              z.number().nullish(),
		inscriptionNumber:               z.string().nullish(),
		date:                            z.date().nullish(),
		psaDate:                         z.coerce.date().nullish(),
		type:                            z.string().nullish(),
		salePrice:                       z.number().nullish(),
		dataRoomLink:                    z.string().nullish(),
		comments:                        z.string().nullish(),
		address:                         z.string().nullish(),
		city:                            z.string().nullish(),
		postalCode:                      z.string().nullish(),
		saleContext:                     z.string().nullish(),
		broker:                          z.string().nullish(),
		agency:                          z.string().nullish(),
		marketplace:                     z.string().nullish(),
		marketplaceId:                   z.string().nullish(),
		mortgage:                        z.boolean().nullish(),
		salesBalance:                    z.boolean(),
		lender:                          z.string().nullish(),
		lenderAmount:                    z.number().nullish(),
		buildingAreaSqft:                z.number().nullish(),
		pricePerBuildingSqft:            z.number().nullish(),
		grossAnnualRent:                 z.number().nullish(),
		grossMonthlyRentPerUnit:         z.number().nullish(),
		grossRentMultiplier:             z.number().nullish(),
		pricePerLandSqft:                z.number().nullish(),
		pricePerDevelopableSqft:         z.number().nullish(),
		pricePerBuildableSqft:           z.number().nullish(),
		pricePerUnit:                    z.number().nullish(),
		constructionYearMin:             z.number().nullish(),
		constructionYearMax:             z.number().nullish(),
		propertyUses:                    z.array(z.object({ code: z.string(), label: z.string() })).nullish(),
		landSubtype:                     z.string().nullish(),
		authorizedUses:                  z.array(z.string().nullish()).nullish(),
		currentState:                    z.string().nullish(),
		tod:                             z.boolean().nullish(),
		zoningPlanLink:                  z.string().nullish(),
		zoningGridLink:                  z.string().nullish(),
		urbanPlanLink:                   z.string().nullish(),
		padLink:                         z.string().nullish(),
		otherZoningDocumentLink:         z.string().nullish(),
		codificationType:                z.string().nullish(),
		projectApproval:                 z.string().nullish(),
		projectApprovalAtDeedOfSale:     z.string().nullish(),
		projectDocument:                 z.string().nullish(),
		projectLink:                     z.string().nullish(),
		project3dModelLink:              z.string().nullish(),
		projectTypes:                    z.array(z.string().nullish()).nullish(),
		stories:                         z.number().nullish(),
		metadataStoriesStatus:           z.nativeEnum(TransactionFieldStatus).nullish(),
		siteCoverageRatio:               z.number().nullish(),
		density:                         z.number().nullish(),
		buildableAreaSqft:               z.number().nullish(),
		metadataBuildableAreaStatus:     z.nativeEnum(TransactionFieldStatus).nullish(),
		numberOfUnits:                   z.number().nullish(),
		metadataNumberOfUnitsStatus:     z.nativeEnum(TransactionFieldStatus).nullish(),
        parkingOutdoorCount:             z.number().nullish(),
        parkingIndoorCount:              z.number().nullish(),
        parkingTypes:                    z.array(z.nativeEnum(TransactionParkingType)).nullish(),
        hasIndoorParking:                z.boolean().nullish(),
        hasOutdoorParking:               z.boolean().nullish(),
        parkingTotal:                    z.number().nullish(),
        parkingRatio:                    z.number().nullish(),
		projectComments:                 z.string().nullish(),
		unitType:                        z.string().nullish(),
		unitAreaSqft:                    z.number().nullish(),
		cptaq:                           z.array(z.string().nullish()).nullish(),
		urbanPerimeter:                  z.array(z.string().nullish()).nullish(),
		infrastructure:                  z.array(z.string().nullish()).nullish(),
		landShape:                       z.string().nullish(),
		parcelReport:                    z.string().nullish(),
		contamination:                   z.string().nullish(),
		contaminationCost:               z.number().nullish(),
		wetlands:                        z.boolean().nullish(),
		floodableZone:                   z.boolean().nullish(),
		endangeredSpecies:               z.boolean().nullish(),
		deductionRatio:                  z.number().nullish(),
		deductionComments:               z.string().nullish(),
		deductionFiles:                  z.array(z.string().nullish()).nullish(),
		developableLandAreaSqft:         z.number().nullish(),
		infrastructureDeductionRatio:    z.number().nullish(),
		infrastructureDeductionComments: z.string().nullish(),
		netLandAreaSqft:                 z.number().nullish(),
		category:                        z.string().nullish(),
		subcategory:                     z.string().nullish(),
		constructabilityComments:        z.string().nullish(),
		classificationComments:          z.string().nullish(),
		buyerRepresentativeLastName:     z.string().nullish(),
		buyerRepresentativeFirstName:    z.string().nullish(),
		sellerRepresentativeLastName:    z.string().nullish(),
		sellerRepresentativeFirstName:   z.string().nullish(),
		ownershipPercentage:             z.number().nullish(),
		adjustedPrice:                   z.number().nullish(),
		hasLegacy:                       z.boolean().nullish(),
		interactiveMapUrl:               z.string().nullish(),
        planMapUrl:                      z.string().nullish(),
        sateliteMapUrl:                  z.string().nullish(),
        zoningImageUrl:                  z.string().nullish(),
        volumetricImageUrl:              z.string().nullish(),
        cityInformationSheetUrl:         z.string().nullish(),
        deedOfSaleUrl:                   z.string().nullish(),
        zoningReportUrl:                 z.string().nullish(),
        landCategory:                    z.string().nullish(),
        assetType: z.array(z.string().nullish()).nullish(),
        builtCategories:                 z.array(z.string()).nullish(),
        builtSubcategories:              z.array(z.string()).nullish(),
        landCategoryNew: z.array(z.string().nullish()).nullish(),
        landSubcategory: z.array(z.string().nullish()).nullish(),
        isDevelopment:                   z.boolean().nullish(),
        isGrossLand:                     z.boolean().nullish(),
        metadataStatus:                  z.string().nullish(),
        metadataBuyerStatus:             z.string().nullish(),
        metadataDoneAt:                  z.coerce.date().nullish(),
        metadataBuyerDoneAt:             z.coerce.date().nullish(),
        driveFolderId:                   z.string().nullish(),
        driveFileCount:                  z.number().nullish(),

	})
	.transform(raw => {
		return {
			...raw,
			presentationId: raw.presentationID,
			id: raw.inscriptionNumber,
			location: {
				...raw.location,
				parcelNumbers: (raw.parcels ?? []).map(parcel => parcel?.parcelID),
				area: raw.superficy?.squareFeet,
			},
			deal: {
				salePrice: raw.salePrice,
				registryDate: raw.deedOfSaleDate ? new Date(raw.deedOfSaleDate) : undefined,
				seller: {
					name: raw.seller?.name,
					neq: raw.seller?.neq,
				},
				buyer: {
					name: raw.buyer?.name,
					neq: raw.buyer?.neq,
				},
			},
            metadata: {
                status: raw.metadataStatus,
                buyerStatus: raw.metadataBuyerStatus,
				storiesStatus: raw.metadataStoriesStatus,
				buildableAreaStatus: raw.metadataBuildableAreaStatus,
				numberOfUnitsStatus: raw.metadataNumberOfUnitsStatus,
                doneAt: raw.metadataDoneAt,
                buyerDoneAt: raw.metadataBuyerDoneAt,
            },
            assetType: raw.assetType,
            landCategoryNew: raw.landCategoryNew,
            landSubcategory: raw.landSubcategory,
		}
	})

export interface Transaction extends z.infer<typeof TransactionSchema> {
	components?: Transaction[] | null
}

/** The Zod schema used to validate a municipality */
export const MunicipalitySchema = z.object({
	name: z.string(),
	regionName: z.string().nullish(),
})

export interface Municipality extends z.infer<typeof MunicipalitySchema> {}

export const BoroughRecordSchema = z.object({
	name: z.string(),
	municipalityName: z.string().nullish(),
})

export interface BoroughRecord extends z.infer<typeof BoroughRecordSchema> {}

export interface Region {
	name: string
}

// prettier-ignore
const SearchFilterSchema = z.object({
	id: z.number().nullish(),
	updatedAt: z.coerce.date(),
	opportunityTypes: z.array(z.string()),
	developmentTypes: z.array(z.string()),
	regions: z.array(z.string()),
	municipalities: z.array(z.string()),
	boroughs: z.array(z.string()),
	price: z.object({
		min: z.number().nullable().transform(v => v ?? 0),
		max: z.number().nullable().transform(v => v ?? Infinity),
	}).default({ min: null, max: null }),
	landArea: z.object({
		min: z.number().nullable().transform(v => v ?? 0),
		max: z.number().nullable().transform(v => v ?? Infinity),
	}).default({ min: null, max: null }),
	buildableLandArea: z.object({
		min: z.number().nullable().transform(v => v ?? 0),
		max: z.number().nullable().transform(v => v ?? Infinity),
	}).default({ min: null, max: null }),
	residentialUnits: z.object({
		min: z.number().nullable().transform(v => v ?? 0),
		max: z.number().nullable().transform(v => v ?? Infinity),
	}).default({ min: null, max: null }),
	launchDate: z.object({
		min: z.coerce.date().nullable().transform(v => v ?? new Date(0)),
		max: z.coerce.date().nullable().transform(v => v ?? new Date(8.64e15)) /* max js date */,
	}).default({ min: null, max: null }),
  assetTypes: z.array(z.string()).default([]),
	landCategories: z.array(z.string()).default([]),
	landSubcategories: z.array(z.string()).default([]),
})

export type SearchFilter = z.infer<typeof SearchFilterSchema>
